<script lang="ts" setup>

import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, useTemplateRef } from 'vue';
import { elevationString } from '../../utils/Utils';
import { KM_TO_MILES } from '@/Util';
import { ProgressMode } from '@/shared/types/types';
import { lighten, parseToHsla, transparentize, darken } from 'color2k';
import { TLineString } from '@/shared/types/geojson';
import { getIcon } from '@/shared/Icons';
import { EventBus } from '@/utils/EventBus';
import Up from "@icons/caret-up.svg"
import { ComputedMarker } from '@/shared/types/zod';
import { getCachedMarkerIcon } from '@/shared/renderers/renderMarkerIcon';
import { progressValues } from '@/utils/markerProgress';

const props = defineProps<{
	progress: number;
	progressMode?: ProgressMode;
	heightData: number[];
	legs: TLineString[];
	colorOverride?: string;
	raceLength: number;
	miles?: boolean;
	overrideMin?: number;
	overrideMax?: number;
	markers?: ComputedMarker[];
}>();

const emit = defineEmits<{
	setProgress: [progress: number, source: ProgressMode];
}>();

const mouseDiv = useTemplateRef("mouseDiv");
const canvas = useTemplateRef("canvas");

const width = ref(400);
const height = ref(40);
const isActive = ref(false);
const wantedProgress = ref(0);
const progressDirty = ref(false);
const timeout = ref<number>(0);

// Hit boxes for clickable markers
type MarkerHitBox = { x: number; y: number; radius: number; marker: ComputedMarker };
const markerHitBoxes = ref<MarkerHitBox[]>([]);
const hoveringMarker = ref(false);
const pointerDownPos = ref<{ x: number; y: number } | null>(null);
let drawRafId: number | null = null;

function scheduleDraw() {
	if (drawRafId != null) return;
	drawRafId = requestAnimationFrame(() => {
		drawRafId = null;
		draw();
	});
}

function getMarkerImage(marker: ComputedMarker): HTMLCanvasElement | undefined {
	const icon = getIcon(marker.properties.icon);
	const color = icon.fixedColor ?? marker.properties.color;
	return getCachedMarkerIcon(icon, color, {
		variation: marker.properties.__markerVariation,
		text: marker.properties.text,
	}, scheduleDraw);
}


function mouseOver(event: MouseEvent) {
	isActive.value = true;
	mouseMove(event);
};

function mouseDown(event: MouseEvent) {
	pointerDownPos.value = { x: event.clientX, y: event.clientY };
};

function touchStart(event: TouchEvent) {
	pointerDownPos.value = { x: event.touches[0].clientX, y: event.touches[0].clientY };
	touchMove(event);
	isActive.value = true;
};

function mouseOut(event: MouseEvent) {
	isActive.value = false;
	wantedProgress.value = 1;
	setDirty();
};

function touchEnd(event: TouchEvent) {
	if (event.changedTouches.length > 0) {
		const touch = event.changedTouches[0];
		tryMarkerClick(touch.clientX, touch.clientY);
	}
	isActive.value = false;
	wantedProgress.value = 1;
	setDirty();
	if (event.cancelable)
		event.preventDefault(); // prevents mouse events which made line reappear on mobile
};

function mouseMove(event: MouseEvent) {
	if (!mouseDiv.value) return;
	const bounds = mouseDiv.value.getBoundingClientRect();
	const x = event.clientX - bounds.left;
	const y = event.clientY - bounds.top;
	const progress = x / mouseDiv.value.clientWidth;
	wantedProgress.value = Math.max(0, Math.min(1, progress));
	hoveringMarker.value = !!findMarkerAtPosition(x, y);
	setDirty();
};


function touchMove(event: TouchEvent) {
	if (!mouseDiv.value) return;
	const bounds = mouseDiv.value.getBoundingClientRect();
	const finger = 50;
	const progress = (event.touches[0].clientX - bounds.left - finger / 2) / (mouseDiv.value.clientWidth - finger);
	wantedProgress.value = Math.max(0, Math.min(1, progress));
	setDirty();
};

function setDirty() {
	if (!progressDirty.value) {
		progressDirty.value = true;
		requestAnimationFrame(updateProgress);
	}
};

function updateProgress() {
	emit("setProgress", wantedProgress.value, isActive.value ? "height" : "");
	progressDirty.value = false;
};

function findMarkerAtPosition(x: number, y: number): ComputedMarker | undefined {
	for (const hitBox of markerHitBoxes.value) {
		const dx = x - hitBox.x;
		const dy = y - hitBox.y;
		if (Math.sqrt(dx * dx + dy * dy) <= hitBox.radius) {
			return hitBox.marker;
		}
	}
	return undefined;
}

function tryMarkerClick(clientX: number, clientY: number) {
	if (!mouseDiv.value) return;

	// Ignore if this was a drag (pointer moved more than 5px)
	if (pointerDownPos.value) {
		const dx = clientX - pointerDownPos.value.x;
		const dy = clientY - pointerDownPos.value.y;
		if (Math.sqrt(dx * dx + dy * dy) > 5) return;
	}

	const bounds = mouseDiv.value.getBoundingClientRect();
	const x = clientX - bounds.left;
	const y = clientY - bounds.top;
	const marker = findMarkerAtPosition(x, y);
	if (marker) {
		EventBus.emit("showPopup", { id: marker.id, fly: true });
	}
}

function handleClick(event: MouseEvent) {
	tryMarkerClick(event.clientX, event.clientY);
}

function elevationAtProgress(progress: number): number {
	if (!props.heightData || props.heightData.length === 0) return 0;
	let after = Math.ceil(progress * (props.heightData.length - 1));
	if (after < 0 || after >= props.heightData.length)
		after = props.heightData.length - 1;

	let before = after > 0 ? after - 1 : 0;
	const beforeFraction = before / (props.heightData.length - 1);
	const afterFraction = after / (props.heightData.length - 1);

	let p = (progress - beforeFraction) / (afterFraction - beforeFraction);
	if (isNaN(p) || !isFinite(p)) // Handle division by zero or NaN
		p = 0;
	let value = (props.heightData[after] - props.heightData[before]) * p + props.heightData[before];
	return value;
};


function draw() {
	if (!canvas.value || props.heightData.length === 0) return;

	const unitMult = props.miles ? 3.2808399 : 1;
	const localCanvas = canvas.value;
	const ctx = localCanvas.getContext("2d");
	if (!ctx) return;

	const oMin = props.overrideMin !== undefined && Number.isFinite(props.overrideMin) ? props.overrideMin : null;
	const oMax = props.overrideMax !== undefined && Number.isFinite(props.overrideMax) ? props.overrideMax : null;
	const dataMinY = oMin !== null ? oMin : props.heightData.reduce((min, p) => p < min ? p : min, props.heightData[0]) * unitMult;
	const dataMaxY = oMax !== null ? oMax : props.heightData.reduce((max, p) => p > max ? p : max, props.heightData[0]) * unitMult;
	const minDiff = oMin != null || oMax != null ? 0.1 : 15;
	const dataDiffY = dataMaxY - dataMinY;
	const diffY = Math.max(dataDiffY, minDiff);
	const paddingY = oMin != null || oMax != null ? 0 : (diffY - dataDiffY) / 2;
	const minY = dataMinY - paddingY;
	const maxY = dataMaxY + paddingY;

	const yPos = (ele: number): number => {
		const v = height.value - ((ele - minY) / diffY * height.value * 0.8) - height.value * 0.05;
		if (Number.isNaN(v))
			return 1;
		return v;
	};

	const getRouteYBounds = (startProgress: number, endProgress: number) => {
		const maxIndex = props.heightData.length - 1;
		const clampedStart = Math.max(0, Math.min(1, startProgress));
		const clampedEnd = Math.max(clampedStart, Math.min(1, endProgress));

		let minRouteY = yPos(elevationAtProgress(clampedStart) * unitMult);
		let maxRouteY = minRouteY;
		const endRouteY = yPos(elevationAtProgress(clampedEnd) * unitMult);
		minRouteY = Math.min(minRouteY, endRouteY);
		maxRouteY = Math.max(maxRouteY, endRouteY);

		const startIndex = Math.max(0, Math.ceil(clampedStart * maxIndex));
		const endIndex = Math.min(maxIndex, Math.floor(clampedEnd * maxIndex));
		for (let i = startIndex; i <= endIndex; i++) {
			const routeY = yPos(props.heightData[i] * unitMult);
			minRouteY = Math.min(minRouteY, routeY);
			maxRouteY = Math.max(maxRouteY, routeY);
		}

		return { minRouteY, maxRouteY };
	};

	ctx.clearRect(0, 0, localCanvas.width, localCanvas.height);
	ctx.globalAlpha = 1;
	ctx.globalCompositeOperation = "destination-over";

	let currentElevationIndex = 0;

	const legsToDraw = props.colorOverride != undefined ? [{
		properties: {
			color: props.colorOverride,
			startProgress: 0,
			endProgress: 1
		}
	}] : props.legs;

	for (let i = 0; i < legsToDraw.length; i++) {
		const leg = legsToDraw[i];
		if (!leg.properties?.color) continue; // Skip if color is missing

		const lightness = parseToHsla(leg.properties.color)[2];
		const change = 0.86 - lightness + (0.1 * lightness);
		const currentColor = leg.properties.color;
		ctx.fillStyle = transparentize(lighten(currentColor, change), .4);

		const startProgress = leg.properties.startProgress ?? 0;
		const endProgress = leg.properties.endProgress ?? 1;

		ctx.beginPath();
		ctx.moveTo(startProgress * width.value, yPos(elevationAtProgress(startProgress) * unitMult));

		let fraction = currentElevationIndex / (props.heightData.length - 1);
		while (currentElevationIndex < props.heightData.length && fraction < endProgress) {
			const pt = props.heightData[currentElevationIndex] * unitMult;
			ctx.lineTo(fraction * width.value, yPos(pt));

			currentElevationIndex++;
			fraction = currentElevationIndex / (props.heightData.length - 1);
		}
		ctx.lineTo(endProgress * width.value, yPos(elevationAtProgress(endProgress) * unitMult));
		ctx.strokeStyle = currentColor;
		ctx.lineWidth = 1.5;
		ctx.stroke();

		ctx.lineTo(endProgress * width.value, height.value);
		ctx.lineTo(startProgress * width.value, height.value);
		ctx.fill();
	}

	ctx.imageSmoothingEnabled = true;
	ctx.globalCompositeOperation = "source-over";

	//horizontal lines
	ctx.beginPath();
	ctx.font = "10px sans-serif"; // Ensure font is set for measureText and fillText/strokeText

	const diff = Math.max(maxY - minY, 1);
	let stepSize = 100;
	if (diff < 5)
		stepSize = 2;
	else if (diff < 100)
		stepSize = 10;
	else if (diff < 1000)
		stepSize = 50;
	let yDist = Math.ceil(diff / 6 / stepSize) * stepSize;
	for (let i = Math.ceil(minY / yDist) * yDist; i < maxY + yDist; i += yDist) { // Adjusted loop condition
		const pos = Math.round(height.value - ((i - minY) / diffY * height.value * 0.8) - height.value * 0.05) - 0.5;
		const text = i.toString();
		const metrics = ctx.measureText(text);
		ctx.moveTo(metrics.width + 5, pos);
		ctx.lineTo(width.value, pos);

		ctx.strokeStyle = "#ffffff";
		ctx.lineWidth = 2;
		ctx.globalAlpha = .65;
		const x = 3;
		ctx.strokeText(text, x, pos + 3.5);
		ctx.globalAlpha = 1;
		ctx.fillStyle = "#000000";
		ctx.fillText(text, x, pos + 3.5);
	}
	ctx.globalAlpha = 0.25;
	ctx.lineWidth = 0.25;
	ctx.strokeStyle = "#000000";
	ctx.stroke();

	// Draw markers on the elevation graph
	markerHitBoxes.value = [];
	if (props.markers && props.markers.length > 0) {
		ctx.save();
		const iconSize = 24; // Size of icons on the elevation graph
		const markerClearance = 3;

		for (const marker of props.markers) {
			if (!marker.properties.showInElevation) continue;
			const progresses = progressValues(marker.properties.__progress);
			if (progresses.length === 0) continue;

			const markerColor = marker.properties.color || '#e69138';

			// Draw a vertical line at the marker position
			// ctx.beginPath();
			// ctx.moveTo(xPos, 0);
			// ctx.lineTo(xPos, height.value);
			// ctx.strokeStyle = darken(markerColor, 0.1);
			// ctx.lineWidth = 1;
			// ctx.globalAlpha = 0.2;
			// ctx.stroke();

			// Get the rendered marker image from the renderer system
			const image = getMarkerImage(marker);
			let imgWidth = iconSize;
			let imgHeight = iconSize;
			if (image) {
				const scale = iconSize / Math.max(image.width, image.height);
				imgWidth = image.width * scale;
				imgHeight = image.height * scale;
			}

			for (const progress of progresses) {
				if (progress < 0 || progress > 1) continue;

				const rawXPos = progress * width.value;
				const halfWidth = imgWidth / 2;
				const halfHeight = imgHeight / 2;
				const xPos = Math.min(Math.max(rawXPos, halfWidth - 2), width.value - halfWidth + 1);
				const footprintStart = (xPos - halfWidth) / width.value;
				const footprintEnd = (xPos + halfWidth) / width.value;
				const { minRouteY, maxRouteY } = getRouteYBounds(footprintStart, footprintEnd);

				const minCenterY = halfHeight;
				const maxCenterY = height.value - halfHeight;
				const preferredAboveY = minRouteY - halfHeight - markerClearance;
				const preferredBelowY = maxRouteY + halfHeight + markerClearance;
				const fitsAbove = preferredAboveY >= minCenterY;
				const fitsBelow = preferredBelowY <= maxCenterY;

				let markerY: number;
				if (fitsAbove) {
					markerY = preferredAboveY;
				} else if (fitsBelow) {
					markerY = preferredBelowY;
				} else {
					const roomAbove = minRouteY;
					const roomBelow = height.value - maxRouteY;
					markerY = roomAbove >= roomBelow
						? minCenterY
						: maxCenterY;
				}

				ctx.globalAlpha = 1;

				if (image) {
					// Draw the rendered marker image centered at the elevation point
					ctx.drawImage(image, xPos - imgWidth / 2, markerY - imgHeight / 2, imgWidth, imgHeight);
				}

				// Store hit box for click detection
				markerHitBoxes.value.push({ x: xPos, y: markerY, radius: iconSize / 2, marker });
			}
		}
		ctx.restore();
	}
};

// sometimes the resize event is triggered before
// the actual size change, check in a bit if we need
// to resize again. (Due to media queries i think)
function handleResizeWithCheck() {
	handleResize();
	if (timeout.value) {
		clearTimeout(timeout.value);
	}
	timeout.value = window.setTimeout(() => {
		if (mouseDiv.value && width.value !== mouseDiv.value.clientWidth) {
			handleResize();
		}
	}, 100);
};

function handleResize() {
	if (!mouseDiv.value || !canvas.value) return;

	height.value = window.innerHeight > 480 ? 90 : 40;
	width.value = mouseDiv.value.clientWidth;

	const localCanvas = canvas.value;
	const ctx = localCanvas.getContext("2d");
	if (!ctx) return;

	var dpr = window.devicePixelRatio || 1;

	localCanvas.style.width = width.value + "px";
	localCanvas.style.height = height.value + "px";
	localCanvas.width = width.value * dpr;
	localCanvas.height = height.value * dpr;
	ctx.scale(dpr, dpr);

	draw();
};


// Computed properties
const currentHeight = computed(() => {
	return elevationAtProgress(props.progress);
});

const currentHeightString = computed(() => {
	return elevationString(currentHeight.value, props.miles ? 'mi' : 'km');
});

const currentDist = computed(() => {
	const mult = props.miles ? KM_TO_MILES : 1;
	// Ensure raceLength is not zero to avoid division by zero or NaN results
	const distValue = props.raceLength > 0 ? (props.progress * props.raceLength / mult) : 0;
	// Use Math.max to ensure non-negative results before flooring
	return (Math.floor(Math.max(0, distValue) * 10) / 10).toFixed(1);
});

const currentDistString = computed(() => `${currentDist.value} ${props.miles ? 'mi' : 'km'}`);

const badgeWidth = computed(() => {
	const longestLineChars = Math.max(
		currentDistString.value.length,
		currentHeightString.value.length + 2,
	);
	return Math.max(40, 10 + longestLineChars * 7);
});

const badgeStyle = computed(() => {
	const widthPx = badgeWidth.value;
	let x = props.progress * width.value - widthPx / 2;
	x = Math.max(0, Math.min(x, width.value - widthPx));

	return {
		'left': x + 'px',
		'width': widthPx + 'px',
	};
});

const showProgress = computed(() => {
	return isActive.value || (props.progressMode != "" && props.progressMode != "map" && props.progressMode != 'height');
});

// Watchers
watch(() => props.heightData, () => {
	nextTick(draw); // Ensure DOM is updated if size depends on data
}, { deep: true });

watch(() => props.miles, () => {
	draw();
});

watch(() => props.colorOverride, () => {
	draw();
});

watch(() => props.overrideMin, () => {
	draw();
});

watch(() => props.overrideMax, () => {
	draw();
});

watch(() => props.legs, () => {
	draw();
}, { deep: true });

watch(() => props.markers, () => {
	draw();
}, { deep: true, immediate: true });

// Lifecycle hooks
onMounted(function () {
	window.addEventListener('resize', handleResizeWithCheck);
	// Ensure the initial size is calculated after the component is mounted
	nextTick(() => {
		handleResize();
		// Initial draw might depend on dimensions set by handleResize
		draw();
	});
});

onBeforeUnmount(function () {
	window.removeEventListener('resize', handleResizeWithCheck);
	if (drawRafId != null) {
		cancelAnimationFrame(drawRafId);
		drawRafId = null;
	}
	if (timeout.value) {
		clearTimeout(timeout.value);
	}
});

</script>


<template>
	<div class="height-curve-outer" @mousedown="mouseDown" @mousemove="mouseMove" @mouseover="mouseOver"
		@mouseout="mouseOut" :style="{ 'height': (height + 10) + 'px' }">
		<div class="height-curve" @touchstart="touchStart" @touchmove="touchMove" @touchend="touchEnd" @click="handleClick"
			ref="mouseDiv" :style="{ 'height': height + 'px', cursor: hoveringMarker ? 'pointer' : 'default' }">

			<div class="height-curve-overflow">
				<canvas ref="canvas"></canvas>

				<div v-if="showProgress" class="progress" :style="{ 'width': progress * 100 + '%' }">
				</div>
			</div>

			<div v-if="showProgress" class="badge" :style="badgeStyle">
				<p>
					{{ currentDist }} {{ miles ? 'mi' : 'km' }}<br />
					<Up style="width: 8px; margin-left: -1px; margin-bottom: 2px;" /> {{ currentHeightString }}
				</p>
			</div>
		</div>
	</div>
</template>


<style scoped lang="scss">
@use "../../style/mixins.scss";

.height-curve-outer {
	@include mixins.unselectable;
	position: absolute;
	bottom: 8px;
	right: 8px;
	left: 8px;
	/* bottom: 0px;
		right: 0px;
		left: 0px; */
	height: 100px;
	/* max-width: 1200px; */
	margin: 0 auto;
	z-index: 1000000001;

	/* background-color: red; */
	touch-action: pan-x;
}

:global(.iframed .height-curve-outer) {
	// background-color: green;
	touch-action: auto;
}

@media (max-height: 300px) {
	.height-curve-outer {
		display: none;
	}
}


.size-m .height-curve,
.size-s .height-curve {
	max-width: unset;
}

.height-curve {
	position: absolute;
	bottom: 0px;
	left: 0px;
	right: 0px;
	margin: 0 auto;
	height: 90px;
	/* max-width: 1000px; */
	background-color: #FFFFFF00;
	touch-action: pan-y;
}

.height-curve-overflow {
	overflow: hidden;
	position: absolute;
	left: -1px;
	bottom: 0;
	right: 0;
	top: 0;
	/* border-radius: 6px; */
	border-radius: 16px;
	border: solid 1px #0006;
	background-color: #ffffff77;
	-webkit-backdrop-filter: blur(6px);
	backdrop-filter: blur(6px);

	box-shadow: 0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%);
}

.progress {
	position: relative;
	height: 100%;
	width: 100%;
	border-right: solid 1px white;
	pointer-events: none;
	box-shadow: 0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%);
	/* background-color: red; */
}

.badge {
	position: absolute;
	top: -30px;
	left: 0;
	display: inline-block;
	background-color: white;
	color: black;
	border-radius: 4px;
	filter: drop-shadow(1px 1px 1px #00000044);
	white-space: nowrap;

	width: 50px;
	overflow: hidden;
	height: 38px;

	pointer-events: none;
}

.badge p {
	padding: 4px 0px;
	margin: 0;
	font-size: .7em;
	font-weight: 700;
	line-height: 1.4em;
	text-align: center;

}

canvas {
	position: absolute;
	overflow: hidden;
}
</style>